/**
 * AttributeRow Component
 * 
 * A single row for entering product attribute data.
 * Provides dropdown for attribute name and input/dropdown for attribute value.
 * 
 * Requirements: 7.2
 */

import React from 'react';
import type { ProductAttribute } from '../types/product';

export interface WooCommerceAttribute {
  id: number;
  name: string;
  slug: string;
  options: string[];
}

interface AttributeRowProps {
  attribute: ProductAttribute;
  index: number;
  availableAttributes: WooCommerceAttribute[];
  onChange: (index: number, field: keyof ProductAttribute, value: string | number | boolean) => void;
  onRemove: (index: number) => void;
  retain?: boolean;
  onRetainToggle?: () => void;
}

export const AttributeRow: React.FC<AttributeRowProps> = ({
  attribute,
  index,
  availableAttributes,
  onChange,
  onRemove,
  retain = false,
  onRetainToggle,
}) => {
  const selectedAttribute = availableAttributes.find(attr => attr.id === attribute.id);
  const hasOptions = selectedAttribute && selectedAttribute.options.length > 0;

  const handleAttributeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = parseInt(e.target.value, 10);
    const selected = availableAttributes.find(attr => attr.id === selectedId);
    
    if (selected) {
      onChange(index, 'id', selected.id);
      onChange(index, 'name', selected.name);
      onChange(index, 'value', ''); // Reset value when attribute changes
    }
  };

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    onChange(index, 'value', e.target.value);
  };

  const handleVisibleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(index, 'visible', e.target.checked);
  };

  return (
    <div className="attribute-row" data-testid={`attribute-row-${index}`}>
      <div className="attribute-field">
        <label htmlFor={`attribute-name-${index}`}>Attribute</label>
        <select
          id={`attribute-name-${index}`}
          value={attribute.id || ''}
          onChange={handleAttributeSelect}
          data-testid={`attribute-name-select-${index}`}
        >
          <option value="">Select attribute...</option>
          {availableAttributes.map(attr => (
            <option key={attr.id} value={attr.id}>
              {attr.name}
            </option>
          ))}
        </select>
      </div>

      <div className="attribute-field">
        <label htmlFor={`attribute-value-${index}`}>Value</label>
        {hasOptions ? (
          <select
            id={`attribute-value-${index}`}
            value={attribute.value}
            onChange={handleValueChange}
            disabled={!attribute.id}
            data-testid={`attribute-value-select-${index}`}
          >
            <option value="">Select value...</option>
            {selectedAttribute.options.map(option => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        ) : (
          <input
            type="text"
            id={`attribute-value-${index}`}
            value={attribute.value}
            onChange={handleValueChange}
            placeholder="Enter value..."
            disabled={!attribute.id}
            data-testid={`attribute-value-input-${index}`}
          />
        )}
      </div>

      <div className="attribute-field attribute-field--checkbox">
        <label htmlFor={`attribute-visible-${index}`}>
          <input
            type="checkbox"
            id={`attribute-visible-${index}`}
            checked={attribute.visible}
            onChange={handleVisibleChange}
            data-testid={`attribute-visible-${index}`}
          />
          Visible
        </label>
      </div>

      <label className="attribute-retain-checkbox">
        <input
          type="checkbox"
          checked={retain}
          onChange={onRetainToggle}
          aria-label={`Keep attribute ${index + 1} for next entry`}
        />
        <span>Keep</span>
      </label>

      <button
        type="button"
        className="attribute-remove-btn"
        onClick={() => onRemove(index)}
        aria-label={`Remove attribute ${index + 1}`}
        data-testid={`attribute-remove-btn-${index}`}
      >
        ✕
      </button>
    </div>
  );
};

export default AttributeRow;
